import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { DatabaseBackup, Download, FlaskConical, RotateCcw } from "lucide-react";
import { API } from "../App";
import { Button } from "./ui/button";
import { Input } from "./ui/input";

const fmtSize = (b) => (b ? `${(b / 1024).toFixed(0)} KB` : "—");

const RestoreTest = ({ t }) => {
  if (!t) return <span className="text-[#94A3B8]">not tested</span>;
  return t.ok
    ? <span className="text-emerald-700">restore verified · {t.documents} records</span>
    : <span className="text-red-700">restore test failed{t.mismatched?.length ? ` (${t.mismatched.join(", ")})` : ""}</span>;
};

// Founder-only: encrypted daily backups, kept 30 days.
export const BackupsCard = () => {
  const [data, setData] = useState(null);
  const [confirmFor, setConfirmFor] = useState(null);
  const [typed, setTyped] = useState("");
  const load = useCallback(() => axios.get(`${API}/founder/backups`).then((r) => setData(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const act = async (fn, msg) => {
    try { await fn(); toast.success(msg); setTimeout(load, 2500); } catch (e) { toast.error(e.response?.data?.detail || "Action failed"); }
  };
  const download = (id) => window.open(`${API}/founder/backups/${id}/download`, "_blank");

  if (!data) return null;
  return (
    <div className="bg-white border border-[#E5DFEC] rounded-2xl p-5 mb-6" data-testid="backups-card">
      <div className="flex items-center gap-2 mb-1">
        <DatabaseBackup className="w-4 h-4 text-[#6B4E8A]" />
        <h3 className="font-medium text-[#6B4E8A]">Database backups</h3>
        <Button size="sm" variant="outline" className="ml-auto" data-testid="backup-now-btn" onClick={() => act(() => axios.post(`${API}/founder/backups`), "Backup started")}>Back up now</Button>
      </div>
      <p className="text-xs text-[#64748B] mb-3">Encrypted, daily at 07:00 UTC, kept {data.retention_days} days. ID and practice-permit documents are never included.</p>
      {data.backups.length === 0 && <p className="text-sm text-[#64748B]" data-testid="backups-empty">No backups yet.</p>}
      <ul className="space-y-2">
        {data.backups.slice(0, 10).map((b) => (
          <li key={b.backup_id} className="text-xs text-[#475569] border-t border-[#F0ECF7] pt-2" data-testid={`backup-row-${b.backup_id}`}>
            <div className="flex flex-wrap items-center gap-2">
              <strong>{b.created_at.slice(0, 16).replace("T", " ")} UTC</strong>
              <span>{b.trigger}</span><span>{b.status}</span><span>{fmtSize(b.size_bytes)}</span>
              <RestoreTest t={b.last_restore_test} />
              {b.restore && <span>restore: {b.restore.status}</span>}
              {b.status === "ok" && (
                <span className="ml-auto flex gap-1">
                  <Button size="sm" variant="ghost" data-testid={`backup-test-${b.backup_id}`} onClick={() => act(() => axios.post(`${API}/founder/backups/${b.backup_id}/restore-test`), "Restore test started")}><FlaskConical className="w-3 h-3 mr-1" />Test restore</Button>
                  <Button size="sm" variant="ghost" data-testid={`backup-download-${b.backup_id}`} onClick={() => download(b.backup_id)}><Download className="w-3 h-3 mr-1" />Download</Button>
                  <Button size="sm" variant="ghost" className="text-red-700" data-testid={`backup-restore-${b.backup_id}`} onClick={() => { setConfirmFor(b.backup_id); setTyped(""); }}><RotateCcw className="w-3 h-3 mr-1" />Restore</Button>
                </span>
              )}
            </div>
            {confirmFor === b.backup_id && (
              <div className="flex items-center gap-2 mt-2" data-testid="restore-confirm-box">
                <span className="text-red-700">Replaces current data (a safety copy is taken first). Type RESTORE:</span>
                <Input value={typed} onChange={(e) => setTyped(e.target.value)} className="h-8 w-32" data-testid="restore-confirm-input" />
                <Button size="sm" disabled={typed !== "RESTORE"} data-testid="restore-confirm-btn" onClick={() => { setConfirmFor(null); act(() => axios.post(`${API}/founder/backups/${b.backup_id}/restore`, { confirm: typed }), "Restore started"); }}>Restore</Button>
              </div>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
};
